import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Client } from '@models/client.model';
import { Project } from '@models/project.model';
import { CreateEditClientModal } from './components/CreateEditClientModal';
import { SecondarySidebar } from '@components/organisms/SecondarySidebar';
import { useQueryClient } from '@tanstack/react-query';
import { useClient, CLIENT_QUERY_KEY } from '@hooks/api/clients/useClient';
import { useClients } from '@hooks/api/clients/useClients';
import { useEditClient } from '@hooks/api/clients/useEditClient';
import { ClientHeader } from './components/ClientHeader';
import { ClientTabBar } from './components/ClientTabBar';
import { ProjectsGrid } from './components/ProjectsGrid';
import { InvoicesPlaceholder } from './components/InvoicesPlaceholder';
import { Loading } from '@components/atoms/Loading';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { EmptyData } from '@components/molecules/EmptyData';
import AddIcon from '@mui/icons-material/Add';
import { useRememberedSelection } from '@hooks/useRememberedSelection';
import { CreateEditProjectModal } from '../projects/components/CreateEditProjectModal';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useFeatureAccess } from '@utils/featureFlag';
import { ClientAccessTab } from './components/ClientAccessTab';
import { UserRoles } from '@models/user.model';
import { ProjectStatus } from '@models/projectStatus.model';
import { useTranslation } from '@hooks/useTranslation';

// Sprint 52, story-269. A client's projects split into Active (everything still in play) and Past
// (finished or dropped). Everything that is not COMPLETED or ABANDONED counts as active.
const PAST_PROJECT_STATES: string[] = [
  ProjectStatus.COMPLETED,
  ProjectStatus.ABANDONED,
];

// Sprint 25, story-128 (run 5). Same set as backend `CLIENT_MANAGEMENT_ROLES`
// (`client.resolver.ts`) — SUPERINTENDENT keeps read access to /clients (D7/UX ruling) but
// never these write actions.
const CLIENT_MANAGEMENT_ROLES: string[] = [
  UserRoles.OWNER,
  UserRoles.ADMIN,
  UserRoles.PROJECT_MANAGER,
];

const ClientsPage: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { isReadOnly, role } = useAppSelector((state: RootState) => state.user);
  const canManageClients = !isReadOnly && CLIENT_MANAGEMENT_ROLES.includes(role as string);
  const [activeTab, setActiveTab] = useState<string>('projects');
  const { canAccess } = useFeatureAccess();

  const { data: clients, isLoading: clientsLoading } = useClients();
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const { data: client, isLoading: clientLoading, isError: clientError } = useClient(clientId);
  const tClients = useTranslation('clients');
  const { activeProjects, pastProjects } = useMemo(() => {
    const all = client?.projects || [];
    const isPast = (p: Project) =>
      PAST_PROJECT_STATES.includes(String(p.state ?? '').toLowerCase());
    return {
      activeProjects: all.filter((p) => !isPast(p)),
      pastProjects: all.filter(isPast),
    };
  }, [client?.projects]);
  const [showNewClientModal, setShowNewClientModal] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<Client | null>(null);
  const { mutateAsync: editClientMutation, isPending: isEditing } =
    useEditClient();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isRemoveProjectModalOpen, setIsRemoveProjectModalOpen] =
    useState<boolean>(false);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [isCreateEditProjectModalOpen, setIsCreateEditProjectModalOpen] =
    useState<boolean>(false);
  const [project, setProject] = useState<Project | undefined>(undefined);

  const getClientId = useCallback((c: Client) => c.clientId, []);

  const { selectedId: rememberedClientId, onSelect: onRememberedSelect } =
    useRememberedSelection<Client>({
      key: 'client',
      items: clients || [],
      getId: getClientId,
      basePath: '/clients',
    });

  useEffect(() => {
    if (rememberedClientId) setClientId(rememberedClientId);
  }, [rememberedClientId]);

  const handleClientSelect = (client: { id: string; name: string }) => {
    onRememberedSelect(client.id);
    setClientId(client.id);
  };

  const handleOpenCreate = () => {
    setInitialData(null);
    setShowNewClientModal(true);
  };

  const handleOpenEdit = () => {
    if (client) {
      setInitialData(client);
      setShowNewClientModal(true);
    }
  };

  const handleOpenEditProject = (projectId: string) => {
    const projectToEdit = client?.projects?.find(
      (p) => p.projectId === projectId,
    );
    setProject(projectToEdit);
    setIsCreateEditProjectModalOpen(true);
  };

  const toggleCreateEditProjectModal = () => {
    setIsCreateEditProjectModalOpen(!isCreateEditProjectModalOpen);
    if (isCreateEditProjectModalOpen) {
      setProject(undefined);
    }
  };

  const handleNavigateToProject = (projectId: string) => {
    history.push(`/projects/${projectId}`);
  };

  // Sprint 31, story-160: project *creation* moved to the full-page wizard, with the client
  // pre-filled via `?clientId=`; the modal stays open only for editing an existing project
  // (`handleOpenEditProject` above).
  const handleCreateProject = () => {
    history.push(clientId ? `/projects/new?clientId=${clientId}` : '/projects/new');
  };

  const handleProjectCreated = () => {
    if (clientId) {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEY(clientId) });
    }
  };

  const handleRemoveProject = async (projectId: string) => {
    if (!client || !client.clientId || !projectId) return;
    const currentProjectIds: string[] = (
      (client.projectIds && client.projectIds.length > 0
        ? client.projectIds
        : (client.projects || [])
            .map((p) => p.projectId)
            .filter((id): id is string => !!id)) || []
    ).filter((id) => id !== projectId);

    setIsRemoveProjectModalOpen(false);

    await editClientMutation({
      clientId: client.clientId,
      name: client.name,
      address: client.address,
      phone: client.phone,
      email: client.email,
      projectIds: currentProjectIds,
      logo: null,
    });
  };

  const handleRemoveProjectConfirmation = (projectId: string) => {
    setIsRemoveProjectModalOpen(true);
    setProjectId(projectId);
  };

  const formattedClients = clients?.map((client) => ({
    id: client.clientId,
    name: client.name,
  }));

  return (
    <div className="flex flex-col h-full">
      <SecondarySidebar
        selectedItem={clientId}
        addButtonLabel={canManageClients ? 'New Client' : undefined}
        items={formattedClients || []}
        toggleModal={canManageClients ? handleOpenCreate : undefined}
        handleMenuItemClick={handleClientSelect}
        disabled={isReadOnly}
      />

      {clientsLoading && (
        <div className="flex justify-center items-center h-full">
          <Loading size="xl" />
        </div>
      )}

      {!clientsLoading && !client && !clientError && (
        <div className="h-full">
          <EmptyData
            textButton={canManageClients ? 'New Client' : undefined}
            description="No Active Clients Yet"
            onClickButton={canManageClients ? handleOpenCreate : undefined}
            iconButton={canManageClients ? <AddIcon sx={{ fontSize: '16px' }} /> : undefined}
            disabled={isReadOnly}
          />
        </div>
      )}

      {!clientsLoading && clientError && (
        <div className="h-full">
          <EmptyData
            textButton="Back to Clients"
            description="Client not found or doesn't belong to this organization"
            onClickButton={() => {
              setClientId(undefined);
              history.push('/clients');
            }}
          />
        </div>
      )}

      {!clientsLoading && client && (
        <div className="flex flex-col h-full">
          <ClientHeader
            client={client}
            onEdit={handleOpenEdit}
            onDelete={() => {
              setIsDeleteModalOpen(true);
            }}
            canManage={canManageClients}
          />

          <ClientTabBar
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab)}
            projectCount={activeProjects.length}
            pastProjectCount={pastProjects.length}
            canViewClientAccess
          />

          {clientLoading || (isEditing && <Loading center size="2xl" />)}

          {!clientLoading && !isEditing && (
            <>
              {activeTab === 'projects' && (
                <ProjectsGrid
                  projects={activeProjects}
                  onEditProject={handleOpenEditProject}
                  onRemoveProject={handleRemoveProjectConfirmation}
                  onNavigateToProject={handleNavigateToProject}
                  onCreateProject={handleCreateProject}
                  canManage={canManageClients}
                  emptyDescription={tClients.noActiveProjectsYet}
                />
              )}
              {activeTab === 'pastProjects' && (
                <ProjectsGrid
                  projects={pastProjects}
                  onEditProject={handleOpenEditProject}
                  onRemoveProject={handleRemoveProjectConfirmation}
                  onNavigateToProject={handleNavigateToProject}
                  canManage={canManageClients}
                  emptyDescription={tClients.noPastProjectsYet}
                  hideCreate
                />
              )}
              {activeTab === 'clientAccess' && client && (
                <ClientAccessTab client={client} canManage={canManageClients} />
              )}
              {activeTab === 'currentInvoices' && canAccess('INVOICES') && (
                <InvoicesPlaceholder />
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}

      <CreateEditClientModal
        isLoading={isEditing}
        initialData={initialData}
        showNewClientModal={showNewClientModal}
        title={initialData ? 'Edit Client' : 'New Client'}
        toggleNewClientModal={() => {
          setShowNewClientModal(!showNewClientModal);
          if (showNewClientModal) setInitialData(null);
        }}
      />

      <ConfirmationModal
        variant="info"
        title="Delete Client"
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        description="if you need to delete a client, please contact with the owner of the project."
      />

      <ConfirmationModal
        title="Remove project from client"
        isOpen={isRemoveProjectModalOpen}
        onClose={() => setIsRemoveProjectModalOpen(false)}
        onConfirm={() => projectId && handleRemoveProject(projectId)}
        description="Are you sure you want to remove this project from the client?"
      />

      {project && (
        <CreateEditProjectModal
          isOpen={isCreateEditProjectModalOpen}
          onClose={toggleCreateEditProjectModal}
          initialData={project}
          onSuccess={handleProjectCreated}
        />
      )}
    </div>
  );
};

export default ClientsPage;
